import type { PropsWithChildren } from 'react';

import { Menu02 } from '@untitledui/icons/Menu02';
import { X as CloseIcon } from '@untitledui/icons/X';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';

import { BASE_PATH } from '@/constants/config';
import { useTheme } from '@/providers/theme-provider';
import { cx } from '@/utils/cx';

export const MobileNavigationHeader = ({
  children,
  logoHref = BASE_PATH,
  showBurgerMenu = true,
}: PropsWithChildren<{ logoHref?: string; showBurgerMenu?: boolean }>) => {
  const { resolvedTheme } = useTheme();

  const logoSrc =
    resolvedTheme === 'dark'
      ? `${BASE_PATH}/logo-light.svg`
      : `${BASE_PATH}/logo-dark.svg`;

  const header = (
    <header className="sticky top-0 z-50 flex h-16 items-center justify-between border-b border-secondary bg-primary py-3 pr-2 pl-4 lg:hidden">
      <a
        aria-label="Go to homepage"
        href={logoHref}
        className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <img src={logoSrc} alt="Respondent" className="h-6" />
      </a>

      {showBurgerMenu && (
        <AriaButton
          aria-label="Expand navigation menu"
          className="group flex items-center justify-center rounded-lg bg-primary p-2 text-fg-secondary outline-focus-ring hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <Menu02 className="size-6 transition duration-200 ease-in-out group-aria-expanded:opacity-0" />
          <CloseIcon className="absolute size-6 opacity-0 transition duration-200 ease-in-out group-aria-expanded:opacity-100" />
        </AriaButton>
      )}
    </header>
  );

  if (!showBurgerMenu) {
    return header;
  }

  return (
    <AriaDialogTrigger>
      {header}

      <AriaModalOverlay
        isDismissable
        className={({ isEntering, isExiting }) =>
          cx(
            'fixed inset-0 z-50 cursor-pointer bg-overlay/70 pr-16 backdrop-blur-md lg:hidden',
            isEntering && 'duration-300 ease-in-out animate-in fade-in',
            isExiting && 'duration-200 ease-in-out animate-out fade-out',
          )
        }
      >
        {({ state }) => (
          <>
            <AriaButton
              aria-label="Close navigation menu"
              onPress={() => {
                state.close();
              }}
              className="fixed top-3 right-2 flex cursor-pointer items-center justify-center rounded-lg p-2 text-fg-white/70 outline-focus-ring hover:bg-white/10 hover:text-fg-white focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <CloseIcon className="size-6" />
            </AriaButton>

            <AriaModal className="w-full cursor-auto will-change-transform">
              <AriaDialog className="h-dvh outline-hidden focus:outline-hidden">
                {children}
              </AriaDialog>
            </AriaModal>
          </>
        )}
      </AriaModalOverlay>
    </AriaDialogTrigger>
  );
};
